import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon-first

// Resolve targets mirror the SLA engine (apps/api/src/sla.ts) so config reads true.
const TARGETS = [
  { priority: "Urgent", coverage: "24/7", resolve: "4 hours (wall-clock)" },
  { priority: "High", coverage: "Business hours", resolve: "1 business day" },
  { priority: "Normal", coverage: "Business hours", resolve: "3 business days" },
  { priority: "Low", coverage: "Business hours", resolve: "5 business days" },
];

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" };
const TIME_INPUT: React.CSSProperties = { fontSize: 12, fontFamily: "'Space Mono',monospace", padding: "3px 6px", border: "1px solid #e2e0d8", borderRadius: 6 };

type HoursData = Awaited<ReturnType<typeof api.hours>>;
type ClientHours = HoursData["clients"][number];

export function Hours() {
  const { data, isLoading, error } = useQuery({ queryKey: ["hours"], queryFn: api.hours });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const now = new Date().toISOString().slice(0, 10);
  const canManage = data.canManage;
  const clientName = new Map(data.clients.map((c) => [c.id, c.name] as const));

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Business hours</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Coverage windows and holidays per client. The SLA engine counts only these open minutes for business-hours
          priorities; urgent runs 24/7. Recomputed every minute by a Cloudflare Cron Trigger.
        </div>
      </div>

      {/* Resolve targets */}
      <div style={CARD}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Icon name="gauge" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Resolve targets</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
          {TARGETS.map((t) => (
            <div key={t.priority} style={{ border: "1px solid #e7e5df", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t.priority}</div>
              <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 2 }}>{t.resolve}</div>
              <div style={{ fontSize: 11, color: "#a8aca6", marginTop: 4 }}>{t.coverage}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-client coverage windows (editable when canManage) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 14 }}>
        {data.clients.map((cl) => <ClientHoursCard key={cl.id} cl={cl} canManage={canManage} />)}
      </div>

      {/* Holiday calendar */}
      <Holidays data={data} canManage={canManage} now={now} clientName={clientName} />
    </Page>
  );
}

type DayState = { closed: boolean; open: string; close: string };

function initDays(cl: ClientHours): Record<number, DayState> {
  const byDay = new Map(cl.windows.map((w) => [w.weekday, w] as const));
  const out: Record<number, DayState> = {};
  for (let wd = 0; wd < 7; wd++) {
    const w = byDay.get(wd);
    const open = w && w.open !== "—";
    out[wd] = open ? { closed: false, open: w!.open, close: w!.close } : { closed: true, open: "09:00", close: "17:00" };
  }
  return out;
}

function ClientHoursCard({ cl, canManage }: { cl: ClientHours; canManage: boolean }) {
  const qc = useQueryClient();
  const [days, setDays] = useState<Record<number, DayState>>(() => initDays(cl));
  const [dirty, setDirty] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.setHours(
        cl.id,
        WEEK_ORDER.map((wd) => ({ weekday: wd, open: days[wd]!.closed ? null : days[wd]!.open, close: days[wd]!.closed ? null : days[wd]!.close })),
      ),
    onSuccess: () => { setDirty(false); qc.invalidateQueries({ queryKey: ["hours"] }); },
  });
  const set = (wd: number, patch: Partial<DayState>) => { setDays((d) => ({ ...d, [wd]: { ...d[wd]!, ...patch } })); setDirty(true); };
  const allClosed = WEEK_ORDER.every((wd) => days[wd]!.closed);
  // An open day must carry two well-formed "HH:MM" times with open < close. Check
  // the format explicitly: a CLEARED <input type="time"> yields "", and "" >= close
  // is false — so a bare `>=` would let an emptied field through, and the server
  // reads open:"" as a *closed* day, silently dropping coverage the user set.
  const timeRe = /^\d{2}:\d{2}$/;
  const badRange = WEEK_ORDER.some((wd) => {
    const d = days[wd]!;
    if (d.closed) return false;
    return !timeRe.test(d.open) || !timeRe.test(d.close) || d.open >= d.close;
  });
  const canSave = dirty && !save.isPending && !allClosed && !badRange;

  return (
    <div style={CARD}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{cl.name}</span>
        <span style={{ fontSize: 11, color: "#8a8e88", fontFamily: "'Space Mono',monospace" }}>{cl.timezone}</span>
      </div>
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
        {WEEK_ORDER.map((wd) => {
          const d = days[wd]!;
          return (
            <div key={wd} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, minHeight: 26 }}>
              <span style={{ color: "#4a504a", width: 40 }}>{DAYS[wd]}</span>
              {!canManage ? (
                <span style={{ color: d.closed ? "#bcbfba" : "#1f231e", fontFamily: "'Space Mono',monospace", fontSize: 11.5 }}>
                  {d.closed ? "Closed" : `${d.open} – ${d.close}`}
                </span>
              ) : d.closed ? (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#8a8e88" }}>
                  <input type="checkbox" checked onChange={() => set(wd, { closed: false })} /> Closed
                </label>
              ) : (
                <>
                  <input type="time" value={d.open} onChange={(e) => set(wd, { open: e.target.value })} style={TIME_INPUT} />
                  <span style={{ color: "#8a8e88" }}>–</span>
                  <input type="time" value={d.close} onChange={(e) => set(wd, { close: e.target.value })} style={TIME_INPUT} />
                  <button onClick={() => set(wd, { closed: true })} title="Mark closed" aria-label={`Mark ${DAYS[wd]} closed`} style={{ marginLeft: 2, display: "inline-flex", alignItems: "center", background: "none", border: 0, cursor: "pointer" }}><Icon name="x" size={13} color="#8a8e88" /></button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {canManage && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => canSave && save.mutate()}
            disabled={!canSave}
            style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: canSave ? "#006747" : "#b7c3bd", border: 0, borderRadius: 7, padding: "6px 14px", cursor: canSave ? "pointer" : "default" }}
          >
            {save.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
          {allClosed && <span style={{ fontSize: 11, color: "#9a7b1f" }}>Keep at least one open day.</span>}
          {!allClosed && badRange && <span style={{ fontSize: 11, color: "#9a7b1f" }}>Each open day needs a start time before its end time.</span>}
          {save.isError && <span style={{ fontSize: 11, color: "#b3261e" }}>{save.error instanceof Error ? save.error.message : "Couldn't save"}</span>}
        </div>
      )}
    </div>
  );
}

function Holidays({ data, canManage, now, clientName }: { data: HoursData; canManage: boolean; now: string; clientName: Map<string, string> }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [scope, setScope] = useState("all");
  const invalidate = () => qc.invalidateQueries({ queryKey: ["hours"] });
  const add = useMutation({ mutationFn: () => api.addHoliday(name.trim(), date, scope), onSuccess: () => { setName(""); setDate(""); setScope("all"); invalidate(); } });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteHoliday(id), onSuccess: invalidate });
  const canAdd = !!name.trim() && /^\d{4}-\d{2}-\d{2}$/.test(date) && !add.isPending;

  return (
    <div style={CARD}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon name="calendar-clock" size={15} color="#c0612f" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Holidays</h2>
        <span style={{ fontSize: 11.5, color: "#8a8e88" }}>SLA clocks pause on these dates</span>
      </div>
      {canManage && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Holiday name" style={{ ...TIME_INPUT, fontFamily: "inherit", flex: "1 1 160px", padding: "6px 9px" }} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...TIME_INPUT, padding: "6px 9px" }} />
          <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ ...TIME_INPUT, fontFamily: "inherit", padding: "6px 9px" }}>
            <option value="all">All clients</option>
            {data.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => canAdd && add.mutate()} disabled={!canAdd} style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: canAdd ? "#006747" : "#b7c3bd", border: 0, borderRadius: 7, padding: "6px 14px", cursor: canAdd ? "pointer" : "default" }}>
            {add.isPending ? "Adding…" : "Add"}
          </button>
          {add.isError && <span style={{ fontSize: 11, color: "#b3261e" }}>{add.error instanceof Error ? add.error.message : "Couldn't add"}</span>}
        </div>
      )}
      {canManage && remove.isError && <div style={{ fontSize: 11, color: "#b3261e", marginBottom: 6 }}>{remove.error instanceof Error ? remove.error.message : "Couldn't remove holiday."}</div>}
      {data.holidays.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No holidays configured.</div>}
      {data.holidays.map((h, i) => {
        const past = h.date < now;
        const removing = remove.isPending && remove.variables === h.id;
        return (
          <div key={h.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "6px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5, opacity: past ? 0.5 : 1 }}>
            <span style={{ fontWeight: 500 }}>{h.name}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "#8a8e88", fontFamily: "'Space Mono',monospace", fontSize: 11.5 }}>
                {h.date}{h.clientId ? ` · ${clientName.get(h.clientId) ?? h.clientId}` : ""}
              </span>
              {canManage && (
                <button onClick={() => !remove.isPending && remove.mutate(h.id)} disabled={remove.isPending} title="Remove" aria-label={`Remove holiday ${h.name}`} style={{ display: "inline-flex", alignItems: "center", background: "none", border: 0, cursor: remove.isPending ? "default" : "pointer", opacity: removing ? 0.4 : 1 }}><Icon name="x" size={13} color="#b3261e" /></button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
